const STORAGE_KEY = "eventsSliceState";

/**
 * Fields that represent "where the user was" and are safe to restore on load.
 * We deliberately exclude derived/transient fields:
 *   - value / eventsOpenedFull  → re-fetched from the server
 *   - status, firstFetch        → loading flags, always reset
 *   - currentPage, lastPage     → pagination always restarts
 *   - stopFetching, pageFetching
 *   - eventGroup01/02, usersList → server data, re-fetched
 */
const PERSISTED_KEYS = [
  "ownerUserId",
  "setID",
  "selectedEventPlatform",
  "platform",
  "showHistory",
  "showRecords",
  "showEmails",
  "showRsvps",
];

/**
 * Reads previously saved events state from sessionStorage.
 * Returns undefined if nothing is saved or parsing fails (safe fallback).
 */
export function loadEventsState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      console.log("[eventsStorage] No saved state found in sessionStorage.");
      return undefined;
    }
    const parsed = JSON.parse(raw);
    // Recently accessed events are intentionally session-memory state. Drop
    // snapshots written by older versions so a browser reload starts clean.
    if (parsed && typeof parsed === "object") {
      delete parsed.eventsOpened;
    }
    console.group("[eventsStorage] Loaded state from sessionStorage");
    console.log(parsed);
    console.groupEnd();
    return parsed;
  } catch (e) {
    console.warn("[eventsStorage] Failed to load state from sessionStorage:", e);
    return undefined;
  }
}

let lastSavedEventsState = null;
let lastSavedSnapshotStr = null;

/**
 * Saves a filtered snapshot of the events slice to sessionStorage.
 * Only the keys listed in PERSISTED_KEYS are written.
 * Optimized with reference and string-comparison checks to prevent unnecessary disk writes.
 */
export function saveEventsState(eventsState) {
  try {
    if (!eventsState) return;

    // Short-circuit if the entire slice state hasn't changed.
    // Redux guarantees a new reference if the state object changes,
    // so this efficiently ignores actions dispatched to other slices.
    if (lastSavedEventsState === eventsState) return;
    lastSavedEventsState = eventsState;

    const snapshot = {};
    PERSISTED_KEYS.forEach((key) => {
      // Write null explicitly for undefined so that "clearing" a key
      // (e.g. setID reset to undefined) is properly persisted and
      // doesn't leave a stale value from a previous session.
      snapshot[key] = eventsState[key] !== undefined ? eventsState[key] : null;
    });

    const currentSnapshotStr = JSON.stringify(snapshot);

    // Only write to sessionStorage (which is blocking) if the *persisted* fields actually changed.
    // This ignores updates to non-persisted fields like `value` or `status` that update frequently.
    if (lastSavedSnapshotStr !== currentSnapshotStr) {
      sessionStorage.setItem(STORAGE_KEY, currentSnapshotStr);
      lastSavedSnapshotStr = currentSnapshotStr;
    }
  } catch (e) {
    console.warn("[eventsStorage] Failed to save state to sessionStorage:", e);
  }
}

/**
 * Clears the persisted events state from sessionStorage.
 * Should be called on logout to prevent stale data being loaded by a different account.
 */
export function clearEventsState() {
  sessionStorage.removeItem(STORAGE_KEY);
  lastSavedEventsState = null;
  lastSavedSnapshotStr = null;
}
